import React from 'react';
import PropTypes from 'prop-types';

const TONES = {
  neutral: { icon: 'tw-bg-slate-500/10 tw-text-slate-600 dark:tw-text-slate-300', dot: 'tw-bg-slate-400', active: 'tw-border-slate-400/60 tw-bg-slate-500/[0.08] dark:tw-border-white/25 dark:tw-bg-white/[0.08]', value: 'tw-text-slate-900 dark:tw-text-white' },
  success: { icon: 'tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-300', dot: 'tw-bg-emerald-500', active: 'tw-border-emerald-500/50 tw-bg-emerald-500/[0.08]', value: 'tw-text-emerald-600 dark:tw-text-emerald-300' },
  info: { icon: 'tw-bg-sky-500/10 tw-text-sky-600 dark:tw-text-sky-300', dot: 'tw-bg-sky-500', active: 'tw-border-sky-500/50 tw-bg-sky-500/[0.08]', value: 'tw-text-sky-600 dark:tw-text-sky-300' },
  warning: { icon: 'tw-bg-amber-500/10 tw-text-amber-600 dark:tw-text-amber-300', dot: 'tw-bg-amber-500', active: 'tw-border-amber-500/50 tw-bg-amber-500/[0.08]', value: 'tw-text-amber-600 dark:tw-text-amber-300' },
  danger: { icon: 'tw-bg-rose-500/10 tw-text-rose-600 dark:tw-text-rose-300', dot: 'tw-bg-rose-500', active: 'tw-border-rose-500/50 tw-bg-rose-500/[0.08]', value: 'tw-text-rose-600 dark:tw-text-rose-300' },
  violet: { icon: 'tw-bg-violet-500/10 tw-text-violet-600 dark:tw-text-violet-300', dot: 'tw-bg-violet-500', active: 'tw-border-violet-500/50 tw-bg-violet-500/[0.08]', value: 'tw-text-violet-600 dark:tw-text-violet-300' },
};

const TILE_BASE = 'tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-gap-1 tw-rounded-[1.25rem] tw-border tw-p-3 tw-text-left sm:tw-gap-2 sm:tw-p-3.5';
const TILE_IDLE = 'tw-border-white/80 tw-bg-white/70 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_12px_32px_-26px_rgba(15,23,42,0.4)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-none';

const TileContent = ({ tile, style }) => {
  const Icon = tile.icon;
  return (
    <>
      <span className={`tw-hidden tw-h-8 tw-w-8 tw-place-items-center tw-rounded-xl sm:tw-grid ${style.icon}`}>
        <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
      </span>
      <span
        className={`tw-max-w-full tw-truncate tw-font-semibold tw-tabular-nums ${
          tile.compact ? 'tw-text-base tw-leading-tight sm:tw-text-xl' : 'tw-text-xl tw-leading-none sm:tw-text-2xl'
        } ${tile.highlight ? style.value : 'tw-text-slate-900 dark:tw-text-white'}`}
      >
        {tile.value}
      </span>
      <span className="tw-flex tw-min-w-0 tw-max-w-full tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">
        <span aria-hidden="true" className={`tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full sm:tw-hidden ${style.dot}`} />
        {/* Phones show the short form */}
        <span className="tw-truncate sm:tw-hidden">{tile.shortLabel || tile.label}</span>
        <span className="tw-hidden tw-truncate sm:tw-inline">{tile.label}</span>
      </span>
    </>
  );
};

TileContent.propTypes = { tile: PropTypes.object.isRequired, style: PropTypes.object.isRequired };

/**
 * Summary counters that double as quick filters (pressing one shows only
 * those records). A tile with `static` is a figure without a filter, e.g. a
 * total amount. `highlight` colours the number (e.g. overdue > 0).
 */
const FilterTiles = ({ tiles, value, onChange, label, className = '' }) => (
  <div role="group" aria-label={label} className={`tw-grid tw-gap-2 sm:tw-gap-3 ${className}`}>
    {tiles.map((tile) => {
      const style = TONES[tile.tone] || TONES.neutral;
      if (tile.static) {
        return (
          <div key={tile.key} title={tile.title} className={`${TILE_BASE} ${TILE_IDLE} ${tile.className || ''}`}>
            <TileContent tile={tile} style={style} />
          </div>
        );
      }
      const active = value === tile.key;
      return (
        <button
          key={tile.key}
          type="button"
          onClick={() => onChange(tile.key)}
          aria-pressed={active}
          className={`${TILE_BASE} tw-transition-[background-color,border-color,transform] tw-duration-200 active:tw-scale-[0.98] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
            active ? style.active : `${TILE_IDLE} hover:tw-bg-white dark:hover:tw-bg-white/[0.06]`
          } ${tile.className || ''}`}
        >
          <TileContent tile={tile} style={style} />
        </button>
      );
    })}
  </div>
);

FilterTiles.propTypes = {
  tiles: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    shortLabel: PropTypes.string,
    value: PropTypes.node,
    icon: PropTypes.elementType.isRequired,
    tone: PropTypes.oneOf(Object.keys(TONES)),
    static: PropTypes.bool,
    compact: PropTypes.bool,
    highlight: PropTypes.bool,
    title: PropTypes.string,
    className: PropTypes.string,
  })).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export default FilterTiles;
